import React, { useRef, useState } from "react";
import { FiChevronDown, FiFolder, FiInbox, FiMessageSquare } from "react-icons/fi";
import { UNFILED } from "../historyService";
import { useOutsidePress } from "../useHistory";

// All conversations, Unfiled, then every folder, each with its count. Shared
// by the rail and by the switcher that replaces it on narrower screens.
function FolderList({ folders, counts, total, loading, error, activeFolder, language, onOpen, onRetry }) {
  const zh = language === "zh";
  // The folder being viewed stays listed after its last conversation leaves,
  // so the page keeps its place; it drops out on the next navigation.
  const keepActive =
    activeFolder &&
    activeFolder !== UNFILED &&
    !folders.some((folder) => folder.toLocaleLowerCase() === activeFolder.toLocaleLowerCase());
  const listed = keepActive ? [...folders, activeFolder].sort((a, b) => a.localeCompare(b)) : folders;
  const filed = Object.values(counts).reduce((sum, count) => sum + count, 0);

  const countOf = (value) => {
    if (total === null) return null;
    if (value === "") return total;
    if (value === UNFILED) return Math.max(0, total - filed);
    if (keepActive && value === activeFolder) return 0;
    return counts[value] ?? null;
  };

  const item = (value, label, Icon) => {
    const active = activeFolder === value;
    const count = countOf(value);
    return (
      <li key={value || "all"}>
        <button
          type="button"
          aria-current={active ? "page" : undefined}
          onClick={() => onOpen(value)}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
            active
              ? "bg-orange-50 text-orange-800 font-semibold dark:bg-orange-500/15 dark:text-orange-200"
              : "text-gray-700 hover:bg-gray-900/5 dark:text-gray-200 dark:hover:bg-white/5"
          }`}
        >
          <Icon aria-hidden="true" className={`w-4 h-4 shrink-0 ${active ? "text-orange-500" : "text-gray-400"}`} />
          <span className="flex-1 min-w-0 break-words">{label}</span>
          {count !== null && (
            <span className={`shrink-0 text-xs font-normal tabular-nums ${active ? "text-orange-700 dark:text-orange-300" : "text-gray-400"}`}>
              {count}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <>
      <ul className="flex flex-col gap-0.5">
        {item("", zh ? "所有對話" : "All conversations", FiMessageSquare)}
        {item(UNFILED, zh ? "未分類" : "Unfiled", FiInbox)}
      </ul>
      <h2 className="mt-4 mb-1 px-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {zh ? "資料夾" : "Folders"}
      </h2>
      {loading && listed.length === 0 ? (
        <p role="status" className="px-2.5 py-1 text-sm text-gray-500 dark:text-gray-400">
          {zh ? "載入資料夾中…" : "Loading folders…"}
        </p>
      ) : listed.length > 0 ? (
        <ul className="flex flex-col gap-0.5">{listed.map((folder) => item(folder, folder, FiFolder))}</ul>
      ) : (
        !error && (
          <p className="px-2.5 py-1 text-sm text-gray-500 dark:text-gray-400">
            {zh ? "尚無資料夾。使用對話上的資料夾按鈕建立第一個。" : "No folders yet. Use the folder button on a conversation to start one."}
          </p>
        )
      )}
      {error && (
        <div role="alert" className="mt-2 px-2.5 text-sm text-red-700 dark:text-red-300">
          {zh ? "無法載入完整資料夾清單。" : "Could not load all folders."}{" "}
          <button type="button" onClick={onRetry} className="underline">
            {zh ? "重試" : "Retry"}
          </button>
        </div>
      )}
    </>
  );
}

// The folder column beside the list, from xl up. It stays in place while the
// list scrolls, so changing folders never means scrolling back to the top.
export function FolderRail(props) {
  return (
    <nav
      aria-label={props.language === "zh" ? "資料夾" : "Folders"}
      className="hidden xl:block sticky top-3 self-start max-h-[calc(100vh-1.5rem)] overflow-y-auto no-scrollbar pt-1"
    >
      <FolderList {...props} />
    </nav>
  );
}

// Below xl the rail folds into this button at the start of the filter bar. It
// also carries the view's name and count, so the page names the view once.
export function FolderSwitcher({ viewTitle, count, ...listProps }) {
  const zh = listProps.language === "zh";
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  useOutsidePress(rootRef, open, () => setOpen(false));

  return (
    <div
      ref={rootRef}
      className="relative xl:hidden"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
          buttonRef.current?.focus();
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={zh ? `資料夾：${viewTitle}` : `Folder: ${viewTitle}`}
        onClick={() => setOpen((value) => !value)}
        className="w-full sm:w-auto sm:max-w-[16rem] inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <FiFolder aria-hidden="true" className="w-4 h-4 shrink-0 text-orange-500" />
        <span className="min-w-0 truncate">{viewTitle}</span>
        {count > 0 && (
          <span className="shrink-0 px-1.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300">
            {count}
          </span>
        )}
        <FiChevronDown
          aria-hidden="true"
          className={`w-4 h-4 shrink-0 ml-auto text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 w-72 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <FolderList
            {...listProps}
            onOpen={(value) => {
              setOpen(false);
              listProps.onOpen(value);
            }}
          />
        </div>
      )}
    </div>
  );
}
